import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    Table,
    Tag,
    message,
    Empty,
    Spin,
    Avatar,
    Button,
    Popconfirm,
    Input,
    Space,
    Tooltip,
    Drawer,
    Descriptions,
    Image,
} from "antd";
import {
    UserOutlined,
    DeleteOutlined,
    CheckCircleFilled,
    WarningFilled,
    MailOutlined,
    PhoneOutlined,
    IdcardOutlined,
    SearchOutlined,
    ReloadOutlined,
    CheckOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";
import CopyText from "../components/CopyText";
import PointsTier from "../components/PointsTier";

const num = (v) => Number(v || 0);

const MyTeamPage = () => {
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [detail, setDetail] = useState(null);
    const [applicants, setApplicants] = useState([]);
    const [applicantsLoading, setApplicantsLoading] = useState(false);
    const [actingId, setActingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.myTeam();
            setTeam(res);
            setSelectedKeys([]);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadApplicants = useCallback(async () => {
        setApplicantsLoading(true);
        try {
            const res = await api.myTeamApplicants();
            setApplicants(res.applicants || []);
        } catch (e) {
            message.error(e.message);
        } finally {
            setApplicantsLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        loadApplicants();
    }, [load, loadApplicants]);

    const [roleActingId, setRoleActingId] = useState(null);
    const setRole = async (userId, role) => {
        setRoleActingId(userId);
        try {
            await api.setMemberRole(userId, role);
            message.success(
                role === "team_lead" ? "Promoted to team lead" : "Set back to volunteer"
            );
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setRoleActingId(null);
        }
    };

    const kick = async (userId) => {
        try {
            await api.removeTeamMember(userId);
            message.success("Member removed");
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const bulkKick = async () => {
        try {
            await Promise.all(selectedKeys.map((id) => api.removeTeamMember(id)));
            message.success(`${selectedKeys.length} members removed`);
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const accept = async (userId) => {
        setActingId(userId);
        try {
            await api.acceptTeamApplicant(userId);
            message.success("Applicant accepted");
            setApplicants((prev) => prev.filter((a) => a._id !== userId));
            load(); // refresh the member roster
        } catch (e) {
            message.error(e.message);
        } finally {
            setActingId(null);
        }
    };

    const reject = async (userId) => {
        setActingId(userId);
        try {
            await api.rejectTeamApplicant(userId);
            message.success("Applicant rejected");
            setApplicants((prev) => prev.filter((a) => a._id !== userId));
        } catch (e) {
            message.error(e.message);
        } finally {
            setActingId(null);
        }
    };

    const applicantColumns = [
        {
            title: "Name",
            key: "name",
            render: (_, u) => (
                <Space>
                    <Avatar size="small" src={u.avatar} icon={<UserOutlined />} />
                    <span>{u.name || "—"}</span>
                </Space>
            ),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (v) => v || "—",
        },
        {
            title: "Points",
            dataIndex: "points",
            key: "points",
            width: 120,
            render: (p) => <PointsTier points={p} />,
        },
        {
            title: "",
            key: "actions",
            width: 190,
            align: "right",
            render: (_, r) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        loading={actingId === r._id}
                        onClick={() => accept(r._id)}
                    >
                        Accept
                    </Button>
                    <Popconfirm
                        title="Reject this applicant?"
                        okText="Reject"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => reject(r._id)}
                    >
                        <Button
                            danger
                            size="small"
                            icon={<CloseOutlined />}
                            loading={actingId === r._id}
                        >
                            Reject
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) return <Spin size="large" style={{ marginTop: 80 }} />;
    if (!team || !team.group)
        return (
            <Empty
                style={{ marginTop: 80 }}
                description="You don't have a team assigned yet."
            />
        );

    const columns = [
        {
            title: "Name",
            key: "name",
            render: (_, u) => (
                <Space>
                    <Avatar size="small" src={u.avatar} icon={<UserOutlined />} />
                    <span>{u.name || "—"}</span>
                    {u.teamRole === "team_lead" && <Tag color="gold">lead</Tag>}
                </Space>
            ),
        },
        {
            title: "Email",
            key: "email",
            render: (_, u) => (
                <Space size={6}>
                    <span>{u.email || "—"}</span>
                    <Tooltip title={u.isMailVerified ? "Email verified" : "Email not verified"}>
                        {u.isMailVerified ? (
                            <CheckCircleFilled style={{ color: "#16a34a" }} />
                        ) : (
                            <WarningFilled style={{ color: "#f59e0b" }} />
                        )}
                    </Tooltip>
                    <Tooltip title={u.isPhoneVerified ? "Phone verified" : "Phone not verified"}>
                        <PhoneOutlined
                            style={{ fontSize: 12, color: u.isPhoneVerified ? "#16a34a" : "#cbd5e1" }}
                        />
                    </Tooltip>
                    <Tooltip title={u.isProfileComplete ? "Profile complete" : "Profile incomplete"}>
                        <IdcardOutlined
                            style={{ fontSize: 12, color: u.isProfileComplete ? "#16a34a" : "#cbd5e1" }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: "Points",
            dataIndex: "points",
            key: "points",
            width: 110,
            sorter: (a, b) => num(a.points) - num(b.points),
            render: (p) => <PointsTier points={p} />,
        },
        { title: "Age", dataIndex: "age", key: "age", width: 80, sorter: (a, b) => num(a.age) - num(b.age) },
        {
            title: "Hours",
            dataIndex: "hours",
            key: "hours",
            width: 90,
            sorter: (a, b) => num(a.hours) - num(b.hours),
            render: (h) => Math.round(num(h)),
        },
        {
            title: "Events",
            dataIndex: "attendedEventsCount",
            key: "attendedEventsCount",
            width: 90,
            sorter: (a, b) => num(a.attendedEventsCount) - num(b.attendedEventsCount),
        },
        {
            title: "Led",
            dataIndex: "eventsLed",
            key: "eventsLed",
            width: 80,
            sorter: (a, b) => num(a.eventsLed) - num(b.eventsLed),
            render: (v) =>
                num(v) > 0 ? <Tag color="gold">★ {num(v)}</Tag> : <span>—</span>,
        },
        {
            title: "Team role",
            key: "role",
            width: 140,
            align: "center",
            render: (_, r) =>
                r.teamRole === "team_lead" ? (
                    <Popconfirm
                        title="Remove team-lead role from this member?"
                        okText="Remove"
                        onConfirm={() => setRole(r._id, "volunteer")}
                    >
                        <Button size="small" loading={roleActingId === r._id}>
                            Remove lead
                        </Button>
                    </Popconfirm>
                ) : (
                    <Popconfirm
                        title="Make this member a team lead? They'll be able to scan attendance in the app."
                        okText="Make lead"
                        onConfirm={() => setRole(r._id, "team_lead")}
                    >
                        <Button size="small" type="primary" ghost loading={roleActingId === r._id}>
                            Make lead
                        </Button>
                    </Popconfirm>
                ),
        },
        {
            title: "",
            key: "remove",
            width: 60,
            align: "right",
            render: (_, r) => (
                <Popconfirm
                    title="Remove this member from the team?"
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => kick(r._id)}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">My Team</h1>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            load();
                            loadApplicants();
                        }}
                    >
                        Refresh
                    </Button>
                    {selectedKeys.length > 0 && (
                        <Popconfirm
                            title={`Remove ${selectedKeys.length} selected members?`}
                            okButtonProps={{ danger: true }}
                            onConfirm={bulkKick}
                        >
                            <Button danger icon={<DeleteOutlined />}>
                                Remove ({selectedKeys.length})
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            </div>

            <Card variant="borderless" style={{ marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>
                    {team.group.name}{" "}
                    {team.group.city && <Tag>{team.group.city}</Tag>}
                </h2>
                {team.group.description && (
                    <p style={{ color: "#64748b", marginBottom: 0 }}>{team.group.description}</p>
                )}
            </Card>

            {/* Applicants — people who requested to join. Accept adds them to the
                team; reject drops their request. (Members can only be added this
                way — there is no add-by-email.) */}
            <Card
                variant="borderless"
                style={{ marginBottom: 16 }}
                title={`Applicants${applicants.length ? ` (${applicants.length})` : ""}`}
            >
                {applicants.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No pending applications"
                    />
                ) : (
                    <Table
                        rowKey="_id"
                        size="small"
                        columns={applicantColumns}
                        dataSource={applicants}
                        loading={applicantsLoading}
                        pagination={false}
                        scroll={{ x: "max-content" }}
                    />
                )}
            </Card>

            <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Search by name or email"
                style={{ width: 280, marginBottom: 16 }}
                onChange={(e) => setSearch(e.target.value)}
            />

            <Table
                rowKey="_id"
                columns={columns}
                dataSource={
                    search.trim()
                        ? team.members.filter((m) =>
                              [m.name, m.email]
                                  .filter(Boolean)
                                  .some((t) =>
                                      t.toLowerCase().includes(search.trim().toLowerCase())
                                  )
                          )
                        : team.members
                }
                scroll={{ x: "max-content" }}
                rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
                onRow={(record) => ({
                    className: "admin-row-clickable",
                    onClick: (e) => {
                        if (
                            e.target.closest(".ant-table-selection-column") ||
                            e.target.closest("button") ||
                            e.target.closest(".ant-checkbox-wrapper") ||
                            e.target.closest(".ant-popover")
                        )
                            return;
                        setDetail(record);
                    },
                })}
                pagination={{ pageSize: 20, showTotal: (t) => `${t} members` }}
            />

            {/* Detail drawer */}
            <Drawer
                open={!!detail}
                onClose={() => setDetail(null)}
                width={440}
                title={detail ? detail.name || detail.email : ""}
            >
                {detail && (
                    <>
                        <div style={{ textAlign: "center", marginBottom: 16 }}>
                            {detail.avatar ? (
                                <Image
                                    src={detail.avatar}
                                    alt={detail.name}
                                    width={96}
                                    height={96}
                                    style={{ borderRadius: "50%", objectFit: "cover" }}
                                    preview={{ mask: "Zoom" }}
                                />
                            ) : (
                                <Avatar size={96} icon={<UserOutlined />} />
                            )}
                        </div>
                        <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Email">
                                <CopyText value={detail.email}>
                                    <span>
                                        <MailOutlined /> {detail.email || "—"}
                                    </span>
                                </CopyText>
                            </Descriptions.Item>
                            <Descriptions.Item label="Phone">
                                <CopyText value={detail.phone}>
                                    <span>
                                        <PhoneOutlined /> {detail.phone || "—"}
                                    </span>
                                </CopyText>
                            </Descriptions.Item>
                            <Descriptions.Item label="Team role">
                                {detail.teamRole === "team_lead" ? (
                                    <Tag color="gold">Team lead</Tag>
                                ) : (
                                    <Tag>Volunteer</Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Age">{detail.age ?? "—"}</Descriptions.Item>
                            <Descriptions.Item label="Gender">{detail.gender || "—"}</Descriptions.Item>
                            <Descriptions.Item label="Points / Rank">
                                <PointsTier points={detail.points} showLabel />
                            </Descriptions.Item>
                            <Descriptions.Item label="Hours">{Math.round(num(detail.hours))}</Descriptions.Item>
                            <Descriptions.Item label="Attended events">{detail.attendedEventsCount ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="Events led as team lead">
                                {num(detail.eventsLed) > 0 ? (
                                    <Tag color="gold">★ {num(detail.eventsLed)}</Tag>
                                ) : (
                                    "—"
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Languages">
                                {(detail.languages || []).length
                                    ? detail.languages.map((l) => <Tag key={l}>{l}</Tag>)
                                    : "—"}
                            </Descriptions.Item>
                            <Descriptions.Item label="OTP attempts">{detail.otpAttempts ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="Created">{fmtDate(detail.createdAt, true)}</Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </Drawer>

        </div>
    );
};

export default MyTeamPage;
